/**
 * Step-level validation logic
 */

import {
    validateName,
    validateEmail,
    validateBirthday,
    validateSex,
    validateCity,
    validateHomeCity,
    validatePassword,
    validateUsername
} from './validators.js';
import { getFieldForStep } from '../steps.js';

export function validateStep(step, state) {
    switch (step) {
        case 1:
            return validateEmail(state.email);
        case 2:
            return validateName(state.name);
        case 3:
            return validateBirthday(state.day, state.month, state.year, state.minAge);
        case 4:
            return validateSex(state.sex);
        case 5:
            return validateCity(state.cityId);
        case 6:
            return validateHomeCity(state.homeCityId);
        case 7:
            return validatePassword(state.password, state.passwordMinLength, state.passwordMaxLength, state.passwordMaxBytes);
        case 8:
            return validateUsername(state.username);
        default:
            return { valid: true };
    }
}

export function isStepValid(step, state) {
    return validateStep(step, state).valid;
}

export function getStepError(step, state) {
    const field = getFieldForStep(step);

    // Don't show errors during initialization
    if (state.isInitializing) {
        return null;
    }

    // Don't show errors until field is touched
    if (!state.touched[field]) {
        return null;
    }

    // Check server errors FIRST (they are authoritative)
    if (state.errors[field]?.message) {
        return state.errors[field].message;
    }
    if (state.errors[field]?.msg) {
        return state.errors[field].msg;
    }
    if (typeof state.errors[field] === 'string') {
        return state.errors[field];
    }

    const result = validateStep(step, state);

    return result.valid ? null : result.error;
}

// Server codes whose refusal gives a sign-in link. The values are ValidationException::CODE_EMAIL_PROVIDER_BANNED
// and CODE_EMAIL_IN_USE.
const SIGN_IN_ERROR_CODES = ['email_provider_banned', 'email_in_use'];

export function stepErrorOffersSignIn(step, state) {
    if (getStepError(step, state) === null) {
        return false;
    }

    const error = state.errors[getFieldForStep(step)];

    return SIGN_IN_ERROR_CODES.includes(error?.code);
}

// The value is EmailProviderAllowedRule::TYPO_FAILURE.
const TYPO_CODE = 'email_domain_typo';

function domainOf(address) {
    return String(address || '').trim().toLowerCase().split('@')[1] || '';
}

// The typo hint names the correction the user is typing, so it stays while a different domain is typed.
function keepsTypoHint(state, field, error) {
    const suggested = domainOf(error.meta?.suggestion);
    const typed = domainOf(state[field]);

    return suggested !== '' && typed !== '' && typed !== suggested;
}

// A server error answers the value that was submitted, so it no longer applies once the field changes.
export function clearServerError(state, field) {
    const error = state.errors[field];
    if (error === undefined || (error?.code === TYPO_CODE && keepsTypoHint(state, field, error))) {
        return;
    }

    const rest = { ...state.errors };
    delete rest[field];
    state.errors = rest;
}

export function getFirstInvalidStep(state, totalSteps) {
    for (let step = 1; step <= totalSteps; step++) {
        if (!isStepValid(step, state)) {
            return step;
        }
    }
    return null;
}

export function markStepTouched(step, state) {
    const field = getFieldForStep(step);
    state.touched[field] = true;
}
